import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import DarkPageContainer from '@/shared/components/DarkPageContainer';
import { Card } from '@/shared/components/Card';
import { PrimaryButton, TertiaryButton } from '@/shared/components/Button';
import { DarkAlert } from '@/shared/components/DarkFormElements';
import { useBuildRequest } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function QuoteApprovalPage() {
  const { authFetch } = useAuthContext();
  const { appId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();

  const actualAppId = appId;

  const [processing, setProcessing] = useState(false);

  // Query hook
  const { data, isLoading: loading, error, refetch } = useBuildRequest(actualAppId);
  
  const request = data?.request ?? null;
  const app = data?.app ?? null;

  useEffect(() => {
    if (error) {
      showToast('Failed to load quote details', 'error');
      navigate('/dashboard');
    }
  }, [error]);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const paymentMethodStatus = searchParams.get('payment_method');
    
    if (paymentStatus === 'cancelled') {
      showToast('Payment was cancelled', 'error');
    }
    
    if (paymentMethodStatus === 'setup') {
      showToast('✅ Payment method set up successfully!', 'success');
      refetch();
    } else if (paymentMethodStatus === 'cancelled') {
      showToast('Payment method setup was cancelled', 'error');
    }
  }, [searchParams]);

  const handlePayDeposit = async () => {
    try {
      setProcessing(true);
      const response = await authFetch('/api/build-requests/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId: actualAppId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment session');
      }

      const data = await response.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data.depositPaid) {
        showToast('✅ Quote accepted!', 'success');
        await loadQuoteDetails();
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      captureError(error, {
        action: 'handleAcceptQuote',
        component: 'QuoteApprovalPage',
        extra: { appId: actualAppId },
      });
      console.error('Error creating payment session:', error);
      showToast(error.message || 'Failed to create payment session', 'error');
      setProcessing(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const returnUrl = `${window.location.origin}/app/${actualAppId}/quote`;
      const response = await authFetch(`/api/users/customer-portal?returnUrl=${encodeURIComponent(returnUrl)}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to open billing portal');
      }
      
      const data = await response.json();
      window.location.href = data.portalUrl;
    } catch (error) {
      captureError(error, {
        action: 'handleOpenBillingPortal',
        component: 'QuoteApprovalPage',
        extra: { appId: actualAppId },
      });
      showToast(error.message || 'Failed to open billing portal', 'error');
    }
  };

  if (loading) {
    return (
      <DarkPageContainer 
        loading={true} 
        loadingText="Loading quote..."
        toasts={toasts}
        onCloseToast={hideToast}
        maxWidth="4xl"
      />
    );
  }

  if (!request || !app || !request.totalAmount) {
    return (
      <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="4xl">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-white mb-2">Quote Not Available</h1>
          <p className="text-gray-400 mb-6">
            This request does not have quote information available.
          </p>
          <PrimaryButton onClick={() => navigate('/dashboard')} size="lg">
            Go to Dashboard
          </PrimaryButton>
        </div>
      </DarkPageContainer>
    );
  }

  const isDepositPaid = !!request.depositPaidAt;
  const totalAmount = parseFloat(request.totalAmount);
  const depositAmount = parseFloat(request.depositAmount);
  const finalAmount = parseFloat(request.finalAmount);
  const monthlyPrice = parseFloat(app.monthlyPrice || app.monthlyMaintenanceFee || '0');
  const oldMonthlyPrice = request.oldMonthlyPrice ? parseFloat(request.oldMonthlyPrice) : null;
  const hasExistingSubscription = !!(app.stripeSubscriptionId && app.status === 'live');
  const isPriceChanging = oldMonthlyPrice !== null && Math.abs(oldMonthlyPrice - monthlyPrice) > 0.01;
  const hasMonthlyFee = monthlyPrice > 0;
  const hasPaymentMethod = app?.hasMonthlyPaymentMethod || false;
  const hasNoDeposit = depositAmount <= 0;
  const hasFullPaymentDue = hasNoDeposit && finalAmount > 0;

  return (
    <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="4xl">
      <Card variant="dark" padding="none" className="overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-zapt-purple to-zapt-purple/80 px-6 sm:px-8 py-10 sm:py-12 text-center text-white">
          <div className="text-6xl mb-4">📋</div>
          <h1 className="text-3xl font-bold mb-2">
            {isDepositPaid ? 'Quote Details' : 'Your Quote is Ready!'}
          </h1>
          <p className="text-purple-100">
            {isDepositPaid 
              ? 'Review your quote details' 
              : hasFullPaymentDue
                ? 'Review your quote and pay to get started'
                : depositAmount > 0
                  ? 'Review your quote and pay the deposit to get started'
                  : 'Review and accept your quote'
            }
          </p>
          {isDepositPaid && (
            <div className="mt-4 inline-block px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg">
              <span className="text-green-200 font-semibold">
                {hasNoDeposit ? '✓ Payment Complete' : '✓ Deposit Paid'}
              </span>
            </div>
          )}
        </div>

        <div className="p-6 sm:p-8">
          {/* App Info */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">{app.name}</h2>
            <p className="text-gray-400">{request.title}</p>
            {request.description && (
              <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{request.description}</p>
              </div>
            )}
          </div>

          {/* Cost Breakdown */}
          <div className="bg-zapt-purple/20 border-2 border-zapt-purple/40 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-6">Cost Breakdown</h3>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 pb-4 border-b border-purple-400/30">
                <span className="text-gray-300">Development Cost</span>
                <span className="text-2xl font-bold text-white">${totalAmount.toFixed(2)}</span>
              </div>

              {hasNoDeposit ? (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div>
                    <div className="font-semibold text-zapt-yellow">Payment due on approval</div>
                    <div className="text-sm text-gray-400">Pay in full when you accept</div>
                  </div>
                  <span className="text-xl font-bold text-zapt-yellow">${finalAmount.toFixed(2)}</span>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div>
                      <div className="font-semibold text-zapt-yellow">Deposit</div>
                      <div className="text-sm text-gray-400">Pay now to start development</div>
                    </div>
                    <span className="text-xl font-bold text-zapt-yellow">${depositAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div>
                      <div className="font-semibold text-gray-300">Final Payment</div>
                      <div className="text-sm text-gray-500">Pay upon completion</div>
                    </div>
                    <span className="text-xl font-semibold text-gray-300">${finalAmount.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Payment Terms */}
          <div className="space-y-3 mb-8">
            <DarkAlert variant="warning" icon="💰" title="Payment Terms">
              {hasNoDeposit 
                ? `Full payment of $${finalAmount.toFixed(2)} due when you accept`
                : 'Deposit now, remaining balance upon project completion'
              }
            </DarkAlert>

            <DarkAlert variant="info" icon="📅" title="Monthly Maintenance">
              <p>
                ${monthlyPrice.toFixed(2)}/month {hasExistingSubscription 
                  ? '(updates your existing subscription)'
                  : 'starts when you complete payment'
                } - includes hosting, updates & support
              </p>
              {hasExistingSubscription && isPriceChanging && (
                <p className="text-xs mt-2 opacity-75">
                  ⚠️ Your monthly subscription will change from ${oldMonthlyPrice.toFixed(2)} to ${monthlyPrice.toFixed(2)}/month
                </p>
              )}
            </DarkAlert>

            <DarkAlert variant="success" icon="🔒" title="Secure Payment">
              Processed securely via Stripe - we never see your card details
            </DarkAlert>
          </div>

          {/* CTA */}
          <div className="border-t border-white/10 pt-6">
            {!isDepositPaid ? (
              <>
                <PrimaryButton
                  onClick={handlePayDeposit}
                  disabled={processing}
                  isLoading={processing}
                  className="w-full"
                  size="xl"
                >
                  {depositAmount > 0 
                    ? `💳 Pay Deposit ($${depositAmount.toFixed(2)})`
                    : hasFullPaymentDue
                      ? hasMonthlyFee
                        ? `💳 Complete Payment ($${finalAmount.toFixed(2)} + $${monthlyPrice.toFixed(2)}/mo)`
                        : `💳 Pay $${finalAmount.toFixed(2)}`
                      : hasMonthlyFee && !hasPaymentMethod 
                        ? '💳 Set Up Payment Method'
                        : '✅ Accept Quote'
                  }
                </PrimaryButton>

                <p className="text-center text-sm text-gray-500 mt-4">
                  {depositAmount > 0 
                    ? "You'll be redirected to Stripe to complete the secure payment"
                    : hasFullPaymentDue
                      ? "You'll be redirected to Stripe to complete the secure payment"
                      : hasMonthlyFee && !hasPaymentMethod
                        ? 'Set up your payment method for monthly maintenance billing (no charge now)'
                        : hasMonthlyFee 
                          ? 'Quote accepted - payment method already set up' 
                          : 'Deposit paid externally - accepting quote to proceed'
                  }
                </p>
              </>
            ) : (
              <>
                <DarkAlert variant="success" icon="✅" title="Quote Accepted" className="mb-6">
                  Your quote has been accepted. Check the app page for project status.
                </DarkAlert>

                {hasMonthlyFee && !hasPaymentMethod && (
                  <DarkAlert variant="warning" icon="💳" title="Set Up Payment Method for Monthly Fees" className="mb-4">
                    <p className="mb-4">
                      To enable automatic monthly billing for ${monthlyPrice.toFixed(2)}/month, please set up a payment method in your billing portal.
                    </p>
                    <button
                      onClick={handleOpenBillingPortal}
                      className="px-6 py-3 bg-yellow-500 text-yellow-900 rounded-lg hover:bg-yellow-400 font-semibold transition-colors"
                    >
                      Set Up Payment Method →
                    </button>
                  </DarkAlert>
                )}

                {hasMonthlyFee && hasPaymentMethod && (
                  <DarkAlert variant="success" icon="✅">
                    Payment method configured. Subscription billing will be charged automatically.
                  </DarkAlert>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Back Link */}
      <div className="text-center mt-6">
        <button
          onClick={() => navigate(`/app/${app.id}`)}
          className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
        >
          Return to App Page
        </button>
      </div>
    </DarkPageContainer>
  );
}
